import React, { useState } from 'react';
import { Alert, AlertTitle, Box, Button, Link } from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { MulticamHowItWorksDialog } from './MulticamHowItWorks';

const DISMISS_KEY = 'pithplay.news.multicam-2026-11';
const PRIVACY_URL = 'https://pithplay.com/privacy';

// Browser storage is a convenience: private windows and blocked storage throw.
const readDismissed = (): boolean => {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === 'dismissed';
  } catch {
    return false;
  }
};

interface MulticamNewsBannerProps {
  /** Starts a new multicam set. */
  onCreate: () => void;
}

/**
 * Tells creators what's new in multicam, once: closing it hides it in this
 * browser. It also says who makes transcripts, since that is new processing
 * of their audio (see the privacy policy's AI section).
 */
const MulticamNewsBanner: React.FC<MulticamNewsBannerProps> = ({ onCreate }) => {
  const [dismissed, setDismissed] = useState(readDismissed);
  const [howOpen, setHowOpen] = useState(false);
  if (dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, 'dismissed');
    } catch {
      // Not remembered; it shows again next visit.
    }
  };

  return (
    <>
      <Alert
        severity="info"
        icon={<AutoAwesomeOutlinedIcon fontSize="inherit" />}
        onClose={dismiss}
        closeText="Dismiss"
        sx={{ mb: 3 }}
        data-testid="multicam-news"
      >
        <AlertTitle sx={{ fontWeight: 700 }}>New in multicam</AlertTitle>
        Your cameras now line up by their sound, you can pick the camera as it plays, and your editor gets a rough cut
        for their editing app. On Pro Max, they also get a transcript, made by speech recognition that Cloudflare runs for us
        (see our{' '}
        <Link href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </Link>
        ).
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
          <Button size="small" variant="outlined" color="inherit" onClick={() => setHowOpen(true)}>
            See how it works
          </Button>
          <Button size="small" color="inherit" onClick={onCreate}>
            Start a multicam set
          </Button>
        </Box>
      </Alert>

      <MulticamHowItWorksDialog open={howOpen} onClose={() => setHowOpen(false)} onCreate={onCreate} />
    </>
  );
};

export default MulticamNewsBanner;
